/**
 * TeamSwitcher — when an org is selected, filter by team (optional).
 */
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useAppStore } from '@/store/appStore';
import type { Id } from '../../convex/_generated/dataModel';
import { Users, ChevronDown, Plus } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

export function TeamSwitcher() {
  const currentAccountId = useAppStore((s) => s.currentAccountId);
  const currentTeamId = useAppStore((s) => s.currentTeamId);
  const setCurrentTeamId = useAppStore((s) => s.setCurrentTeamId);
  const [open, setOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const teams = useQuery(
    api.teams.listByOrg,
    currentAccountId ? { organizationId: currentAccountId as Id<'organizations'> } : 'skip'
  );
  const createTeam = useMutation(api.teams.create);

  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', fn);
    return () => document.removeEventListener('click', fn);
  }, []);

  const handleCreateTeam = async () => {
    const name = createName.trim() || 'New team';
    if (!currentAccountId) return;
    try {
      await createTeam({
        organizationId: currentAccountId as Id<'organizations'>,
        name,
      });
      setCreateName('');
      setOpen(false);
    } catch (err) {
      console.error('Create team failed:', err);
    }
  };

  if (!currentAccountId || !teams) return null;

  const currentTeam = currentTeamId ? teams.find((t: any) => t._id === currentTeamId) : null;
  const displayLabel = currentTeam ? currentTeam.name : 'All teams';

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted/50"
      >
        <Users className="h-4 w-4" />
        <span className="truncate max-w-[100px]">{displayLabel}</span>
        <ChevronDown className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 w-48 rounded-lg border border-border bg-card shadow-lg py-1 z-50">
          <button
            onClick={() => {
              setCurrentTeamId(null);
              setOpen(false);
            }}
            className={`flex items-center gap-2 w-full px-3 py-2 text-left text-sm hover:bg-muted/50 ${!currentTeamId ? 'bg-muted/50' : ''}`}
          >
            <Users className="h-4 w-4" />
            All teams
          </button>
          {teams.map((t: any) => (
            <button
              key={t._id}
              onClick={() => {
                setCurrentTeamId(t._id);
                setOpen(false);
              }}
              className={`flex items-center gap-2 w-full px-3 py-2 text-left text-sm hover:bg-muted/50 ${currentTeamId === t._id ? 'bg-muted/50' : ''}`}
            >
              <span className="truncate">{t.name}</span>
            </button>
          ))}
          <div className="border-t border-border mt-1 pt-1 px-2">
            <input
              type="text"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              placeholder="New team name"
              className="w-full rounded border border-input bg-background px-2 py-1.5 text-xs mb-1"
            />
            <button
              onClick={handleCreateTeam}
              className="flex items-center gap-2 w-full rounded bg-primary/10 text-primary px-2 py-1.5 text-xs hover:bg-primary/20"
            >
              <Plus className="h-3 w-3" />
              Create team
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
